import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { actions } from 'modules/user';
import track from '../../../../../../lib/track.js';
import './styles.css';
import { intakeGroups, finalScreen, buildIntakeSteps } from './intakeConfig';
import { useWLC } from '../../contexts/WLCContext/WLCContext';
import GroupIntro from './components/IntakeForm/GroupIntro';
import QuestionStep from './components/IntakeForm/QuestionStep';
import FinalScreen from './components/IntakeForm/FinalScreen';
import IntakeProgress from './components/IntakeForm/IntakeProgress';

const DEFAULT_ERROR = 'Please make a selection.';

const getQuestionMap = groups => {
  const map = {};
  groups.forEach(group => {
    group.questions.forEach(question => {
      map[question.id] = question;
    });
  });
  return map;
};

const getGroupMap = groups => {
  const map = {};
  groups.forEach(group => {
    map[group.id] = group;
  });
  return map;
};

const getAnswerValue = answer => {
  if (!answer) return null;
  return answer.value;
};

const isEmptySelection = answerValue => {
  if (!answerValue) return true;
  if (Array.isArray(answerValue)) return answerValue.length === 0;
  return false;
};

const validateQuestion = (question, answer) => {
  if (!question) return null;
  if (question.type === 'date') return null;

  const value = getAnswerValue(answer);
  if (question.required && isEmptySelection(value)) {
    return question.errorMessage || DEFAULT_ERROR;
  }

  if (question.maxSelections && Array.isArray(value) && value.length > question.maxSelections) {
    return question.maxErrorMessage || `Please only pick up to ${question.maxSelections} topics.`;
  }

  if (question.otherOption && value) {
    const isOtherSelected = Array.isArray(value)
      ? value.includes(question.otherOption.value)
      : value === question.otherOption.value;
    if (isOtherSelected && question.otherOption.required && !answer?.otherText?.trim()) {
      return question.otherOption.errorMessage || 'Please add a short note.';
    }
  }

  return null;
};

const getAffirmationText = (question, answer) => {
  if (!question || !answer) return '';
  if (typeof question.getAffirmation === 'function') {
    return question.getAffirmation(answer);
  }

  const value = getAnswerValue(answer);
  if (!value || (Array.isArray(value) && value.length === 0)) return '';

  if (question.affirmationByValue && !Array.isArray(value)) {
    return question.affirmationByValue[value] || '';
  }

  return question.affirmation || '';
};

function Onboarding(props) {
  const ONBOARDING_ID = process.env.ONBOARDING_ID;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [touched, setTouched] = useState({});
  const { isOnboard, isLegacyMember, wlcProgress, updateWlcProgress, completeOnboarding } = useWLC();

  const userAnalytics = {
    service: 'dashboard',
    slug: props.slug ? props.slug : '',
    title: props.pageTitle ? props.pageTitle : 'wlc-onboarding-intake',
    uuid: props.user && props.user.UUID
  };

  const steps = useMemo(() => buildIntakeSteps(intakeGroups), []);
  const questionMap = useMemo(() => getQuestionMap(intakeGroups), []);
  const groupMap = useMemo(() => getGroupMap(intakeGroups), []);
  const currentStep = steps[currentStepIndex];

  // Count total questions (exclude intro and final steps)
  const totalQuestions = useMemo(() => {
    return steps.filter(step => step.type === 'question').length;
  }, [steps]);

  // Calculate current question number (1-indexed)
  const currentQuestionNumber = useMemo(() => {
    let count = 0;
    for (let i = 0; i <= currentStepIndex; i++) {
      if (steps[i]?.type === 'question') {
        count++;
      }
    }
    return count;
  }, [currentStepIndex, steps]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadUserData = async () => {
    const userState = await dispatch(actions.getUserData(ONBOARDING_ID));
    if (userState?.intakeAnswers) {
      setAnswers(userState.intakeAnswers);
    }
    if (Number.isInteger(userState?.intakeCurrentStep)) {
      setCurrentStepIndex(Math.min(userState.intakeCurrentStep, steps.length - 1));
    }
    setLoading(false);
  };

  useEffect(() => {
    loadUserData();
  }, [dispatch, ONBOARDING_ID, steps.length]);

  useEffect(() => {
    if (loading) return;
    const newState = {
      intakeAnswers: answers,
      intakeCurrentStep: currentStepIndex
    };
    dispatch(actions.postUserData(ONBOARDING_ID, newState));
  }, [answers, currentStepIndex, dispatch, loading, ONBOARDING_ID]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.resetOnboarding = () => {
      dispatch(actions.postUserData(ONBOARDING_ID, {}));
    };
    if (window.location.search.includes('resetOnboarding')) {
      window.resetOnboarding();
    }
    return () => delete window.resetOnboarding;
  }, []);

  const updateAnswer = useCallback((questionId, updates) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        ...updates
      }
    }));
  }, []);

  const handleSingleSelect = useCallback(
    (questionId, value) => {
      updateAnswer(questionId, { value });
    },
    [updateAnswer]
  );

  const handleMultiToggle = useCallback((question, optionValue) => {
    setAnswers(prev => {
      const existing = prev[question.id]?.value || [];
      const isSelected = existing.includes(optionValue);
      let next = isSelected ? existing.filter(item => item !== optionValue) : [...existing, optionValue];

      if (question.exclusiveOptions?.includes(optionValue) && !isSelected) {
        next = [optionValue];
      }

      if (question.exclusiveOptions?.length && !isSelected && !question.exclusiveOptions.includes(optionValue)) {
        next = next.filter(item => !question.exclusiveOptions.includes(item));
      }

      const nextAnswer = {
        ...(prev[question.id] || {}),
        value: next
      };

      if (question.otherOption && !next.includes(question.otherOption.value)) {
        nextAnswer.otherText = '';
      }

      return {
        ...prev,
        [question.id]: nextAnswer
      };
    });

    // Show error immediately if max selections exceeded
    if (question.maxSelections) {
      setTouched(prev => ({ ...prev, [question.id]: true }));
    }
  }, []);

  const handleOtherTextChange = useCallback(
    (questionId, value) => {
      updateAnswer(questionId, { otherText: value });
    },
    [updateAnswer]
  );

  const handleDateChange = useCallback((questionId, field, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        date: {
          ...(prev[questionId]?.date || {}),
          [field]: value
        }
      }
    }));
  }, []);

  const goToStepIndex = useCallback(nextIndex => {
    setCurrentStepIndex(nextIndex);
    scrollToTop();
  }, []);

  const handleNext = useCallback(() => {
    const currentStep = steps[currentStepIndex];
    if (!currentStep) return;

    if (currentStep.type === 'question') {
      const question = questionMap[currentStep.questionId];
      const error = validateQuestion(question, answers[question.id]);
      if (error) {
        setTouched(prev => ({ ...prev, [question.id]: true }));
        return;
      }
    }

    if (currentStep.type === 'intro' && currentStepIndex === 0) {
      track('onboarding', { ...userAnalytics, ...{ step: 'begin' } });
    }
    console.log(
      'ONBOARDING',
      currentStep.questionId,
      wlcProgress,
      currentStep.questionId === 'priority-track' &&
        (!wlcProgress?.currentTrack || wlcProgress?.currentTrack === 'wlc-track-introductory')
    );

    if (
      currentStep.questionId === 'priority-track' &&
      (isLegacyMember || !wlcProgress?.currentTrack || wlcProgress?.currentTrack === 'wlc-track-introductory')
    ) {
      const selectedTrack = answers[currentStep.questionId]?.value;
      const DEFAULT_TRACK = 'wlc-track-healthy-eating';
      const TRACK_MAP = {
        'wlc-track-gut-health': [
          'wlc-track-gut-health',
          'wlc-track-healthy-eating',
          'wlc-track-brain-health',
          'wlc-track-heart-health',
          'wlc-track-blood-sugar'
        ],
        'wlc-track-heart-health': [
          'wlc-track-heart-health',
          'wlc-track-healthy-eating',
          'wlc-track-brain-health',
          'wlc-track-gut-health',
          'wlc-track-blood-sugar'
        ],
        'wlc-track-brain-health': [
          'wlc-track-brain-health',
          'wlc-track-healthy-eating',
          'wlc-track-gut-health',
          'wlc-track-heart-health',
          'wlc-track-blood-sugar'
        ],
        'wlc-track-blood-sugar': [
          'wlc-track-blood-sugar',
          'wlc-track-healthy-eating',
          'wlc-track-gut-health',
          'wlc-track-heart-health',
          'wlc-track-brain-health'
        ],
        'wlc-track-healthy-eating': [
          'wlc-track-healthy-eating',
          'wlc-track-heart-health',
          'wlc-track-brain-health',
          'wlc-track-gut-health',
          'wlc-track-blood-sugar'
        ]
      };
      const nextTrackPriorityOrder = TRACK_MAP[selectedTrack] || TRACK_MAP[DEFAULT_TRACK];

      if (isLegacyMember) {
        const currentTrack = wlcProgress?.currentTrack;
        const legacyTrackPriorityOrder = currentTrack
          ? [currentTrack, ...nextTrackPriorityOrder.filter(track => track !== currentTrack)]
          : nextTrackPriorityOrder;

        updateWlcProgress({
          trackPriorityOrder: legacyTrackPriorityOrder
        });
      } else {
        updateWlcProgress({
          ...wlcProgress,
          currentTrack: 'wlc-track-introductory',
          currentWeekSlug: 'getting-started',
          currentTrackIndex: 0,
          trackProgress: {
            ...wlcProgress?.trackProgress,
            'wlc-track-introductory': {
              currentTrackIndex: 0,
              trackLength: 1,
              currentWeekSlug: 'getting-started',
              completedWeeks: []
            }
          },
          trackPriorityOrder: nextTrackPriorityOrder
        });
      }
    }

    goToStepIndex(Math.min(currentStepIndex + 1, steps.length - 1));
  }, [
    answers,
    currentStepIndex,
    goToStepIndex,
    isLegacyMember,
    questionMap,
    steps,
    userAnalytics,
    wlcProgress,
    updateWlcProgress
  ]);

  const handlePrev = useCallback(() => {
    if (currentStepIndex === 0) return;
    goToStepIndex(currentStepIndex - 1);
  }, [currentStepIndex, goToStepIndex]);

  const onFinishPrimary = useCallback(() => {
    completeOnboarding();
    if (props.handleNav) {
      props.handleNav('current-week');
      return;
    }
    if (props.history?.push) {
      props.history.push('/products/wlc/current-week');
    }
  }, [completeOnboarding, props]);

  const handleShowFeatureOnboarding = useCallback(() => {
    if (props.handleNav) {
      props.handleNav('getting-started');
      return;
    }
    if (props.history?.push) {
      props.history.push('/products/wlc/getting-started');
    }
  }, [props]);

  if (loading) {
    return (
      <div className="intake-container">
        <div className="intake-loading">Loading your onboarding...</div>
      </div>
    );
  }

  if (!currentStep) return null;

  if (currentStep.type === 'final') {
    return (
      <div className="intake-container">
        <IntakeProgress
          currentQuestion={totalQuestions}
          totalQuestions={totalQuestions}
          customMessage="You did it!"
          showConfetti
        />
        <FinalScreen {...finalScreen} onPrimary={onFinishPrimary} />
      </div>
    );
  }

  if (currentStep.type === 'intro') {
    const group = groupMap[currentStep.groupId];

    return (
      <div className="intake-container">
        <IntakeProgress
          currentQuestion={currentQuestionNumber || 1}
          totalQuestions={totalQuestions}
          customMessage={group?.introMessage || ''}
        />
        <GroupIntro
          title={currentStep.title}
          subtitle={currentStep.subtitle}
          paragraphs={currentStep.paragraphs}
          buttonText={currentStep.buttonText}
          onNext={handleNext}
          onBack={currentStepIndex > 0 ? handlePrev : null}
          groupLabel={group?.label}
        />
      </div>
    );
  }

  const question = questionMap[currentStep.questionId];
  const answer = answers[question.id];
  const errorMessage = validateQuestion(question, answer);
  const showError = Boolean(touched[question.id] && errorMessage);
  const affirmationText = getAffirmationText(question, answer);

  return (
    <div className="intake-container">
      <IntakeProgress currentQuestion={currentQuestionNumber} totalQuestions={totalQuestions} />
      <QuestionStep
        question={question}
        answer={answer}
        affirmation={affirmationText}
        errorMessage={showError ? errorMessage : ''}
        onBack={currentStepIndex > 0 ? handlePrev : null}
        onNext={handleNext}
        onSingleSelect={handleSingleSelect}
        onMultiToggle={handleMultiToggle}
        onOtherTextChange={handleOtherTextChange}
        onDateChange={handleDateChange}
      />
    </div>
  );
}

Onboarding.propTypes = {
  handleNav: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func
  }),
  pageTitle: PropTypes.string,
  slug: PropTypes.string,
  user: PropTypes.shape({
    UUID: PropTypes.string
  })
};

export default Onboarding;
